import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import classNames from 'classnames';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { IconButton, RevelioTooltip, Text } from '@revelio/ui';

import { useEscapePeel } from '../../shared/hooks/useEscapePeel';
import {
  GLOBAL_AI_OVERLAY_BOTTOM,
  GLOBAL_AI_OVERLAY_HEIGHT,
  GLOBAL_AI_OVERLAY_RIGHT,
  GLOBAL_AI_OVERLAY_WIDTH,
  GLOBAL_AI_OVERLAY_Z_INDEX,
} from '../CustomDash/constants/panelWidths';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import { GLOBAL_AI_ASSISTANT_GREETING } from './global-ai-assistant-copy';
import styles from './global-ai-assistant-drawer.module.css';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import {
  GlobalAiChatInput,
  type GlobalAiChatInputHandle,
} from './global-ai-chat-input';
import { GlobalAiChatTranscriptPane } from './global-ai-chat-transcript-pane';
import { GlobalAiConversationList } from './global-ai-conversation-list';
import { SidebarToggleGlyph } from './global-ai-conversation-sidebar';

/**
 * Floating chat skin over the shared assistant engine. All conversation
 * logic (turn running, submission, navigation/apply/report actions,
 * continuations) lives in `GlobalAiAssistantEngine`, published through
 * `useGlobalAiEngineStore` — this component renders drawer chrome (header,
 * ESC handling, fixed positioning) around the shared transcript pane and
 * chat input. The assistant homepage renders the same conversation through
 * its own skin.
 */
export function GlobalAIAssistantDrawer() {
  const { isDrawerOpen, closeDrawer, persistence } = useGlobalAIAssistant();

  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);
  const isReportOfferLaunching = useGlobalAiEngineStore(
    (s) => s.reportOfferLaunchingKey !== null
  );

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const chatInputRef = useRef<GlobalAiChatInputHandle>(null);
  const chatColumnRef = useRef<HTMLDivElement>(null);
  const historyToggleRef = useRef<HTMLButtonElement>(null);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const prevIsHistoryOpen = useRef(false);

  const onCloseAndStopRecording = useCallback(() => {
    chatInputRef.current?.stopRecording();
    setIsHistoryOpen(false);
    closeDrawer();
  }, [closeDrawer]);

  // ESC peels one layer at a time: history panel first, then the drawer.
  useEscapePeel({
    enabled: isDrawerOpen,
    isInnerOpen: isHistoryOpen,
    closeInner: () => setIsHistoryOpen(false),
    closeOuter: onCloseAndStopRecording,
  });

  // Push-over focus management. While history is the active layer the chat
  // column is taken out of the tab order AND the a11y tree via `inert` (set
  // imperatively — React 18 has no `inert` prop), and focus is moved into the
  // panel; on close it returns to the toggle. Without this, opening history
  // leaves focus on the now-hidden toggle (an aria-hidden-on-focused-element
  // violation) and closing via the scrim/ESC drops focus to <body>. Order
  // matters: set `inert` first, then move focus, so the target is reachable.
  useEffect(() => {
    const chatColumn = chatColumnRef.current;
    if (chatColumn) {
      chatColumn.inert = isHistoryOpen;
    }
    if (isHistoryOpen && !prevIsHistoryOpen.current) {
      historyPanelRef.current?.focus();
    } else if (!isHistoryOpen && prevIsHistoryOpen.current) {
      historyToggleRef.current?.focus();
    }
    prevIsHistoryOpen.current = isHistoryOpen;
  }, [isHistoryOpen]);

  const handleNewSession = () => {
    persistence.newChat();
    chatInputRef.current?.clearDraft();
    setIsHistoryOpen(false);
  };

  return (
    <div
      className={styles.drawer}
      style={
        {
          '--drawer-right': GLOBAL_AI_OVERLAY_RIGHT,
          '--drawer-bottom': GLOBAL_AI_OVERLAY_BOTTOM,
          '--drawer-width': GLOBAL_AI_OVERLAY_WIDTH,
          '--drawer-height': GLOBAL_AI_OVERLAY_HEIGHT,
          '--drawer-z-index': GLOBAL_AI_OVERLAY_Z_INDEX,
        } as CSSProperties
      }
      data-open={isDrawerOpen}
      aria-hidden={isDrawerOpen ? undefined : true}
    >
      {/* The chat column (header included) slides RIGHT when history opens,
          so the strip that stays visible is the chat's LEFT edge (avatar +
          line starts), matching the Figma push-over layout — not the right
          edge an in-place overlay would expose. */}
      <div
        ref={chatColumnRef}
        className={classNames(
          `${styles.chatColumn} ${
            isHistoryOpen ? styles.chatColumnShifted : ''
          }`,
          styles.chatLayout
        )}
      >
        <div className={classNames(styles.headerBorder, styles.header)}>
          <IconButton
            aria-label="New chat"
            icon={<IconPlusSmall size={16} />}
            size="sm"
            shape="rounded"
            className={classNames(styles.iconBtn, styles.compactIconBtn)}
            onClick={handleNewSession}
            disabled={isTurnRunning || isReportOfferLaunching}
          />
          <RevelioTooltip
            label={isHistoryOpen ? 'Hide chat history' : 'Show chat history'}
            placement="bottom"
          >
            <IconButton
              ref={historyToggleRef}
              aria-label={
                isHistoryOpen ? 'Hide chat history' : 'Show chat history'
              }
              // Same treatment as the homepage sidebar toggle: static
              // open-state glyph (no flip); the ChatGPT-style resize cursor
              // points the way the history panel will move.
              icon={<SidebarToggleGlyph />}
              data-resize-cursor={isHistoryOpen ? 'w' : 'e'}
              size="sm"
              shape="rounded"
              className={classNames(styles.iconBtn, styles.compactIconBtn)}
              onClick={() => setIsHistoryOpen((prev) => !prev)}
              disabled={isTurnRunning || isReportOfferLaunching}
              data-testid="global-ai-history-toggle"
            />
          </RevelioTooltip>
          <Text
            as="h2"
            fontSize="md"
            weight="semibold"
            lineClamp={1}
            className={`${styles.text} ${styles.headerTitle}`}
          >
            AI Chat
          </Text>
          <IconButton
            aria-label="Close Chat"
            icon={<IconCrossLarge size={12} />}
            size="md"
            shape="rounded"
            className={styles.iconBtn}
            onClick={onCloseAndStopRecording}
          />
        </div>

        <div className={styles.chatBody}>
          <GlobalAiChatTranscriptPane variant="drawer" />

          <div className={styles.inputArea}>
            <GlobalAiChatInput
              ref={chatInputRef}
              testId="global-ai-assistant-prompt"
              placeholder={GLOBAL_AI_ASSISTANT_GREETING}
            />
          </div>
        </div>
      </div>

      {/* Scrim greys the pushed-aside chat strip; clicking it closes history. */}
      <button
        type="button"
        aria-label="Close chat history"
        tabIndex={isHistoryOpen ? 0 : -1}
        className={`${styles.historyScrim} ${
          isHistoryOpen ? styles.historyScrimVisible : ''
        }`}
        onClick={() => setIsHistoryOpen(false)}
        data-testid="global-ai-history-scrim"
      />

      {/* History pulls over from the LEFT, covering ~80% of the drawer. */}
      <div
        ref={historyPanelRef}
        tabIndex={-1}
        aria-label="Chat history"
        className={classNames(
          `${styles.historyPanel} ${
            isHistoryOpen ? styles.historyPanelOpen : ''
          }`,
          styles.globalAiHistoryPanel
        )}
        aria-hidden={isHistoryOpen ? undefined : true}
        data-testid="global-ai-history-panel"
      >
        <GlobalAiConversationList onSelected={() => setIsHistoryOpen(false)} />
      </div>
    </div>
  );
}
